import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { EMPTY_MATERIAL_ROW, type SupplyMaterialRow } from '../model';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Title = styled.div`
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 110px 90px auto;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

    & > :first-child {
      grid-column: 1 / -1;
    }
  }
`;

/** Подписи колонок; на узком экране строки перестраиваются — там хватает подсказок в полях. */
const HeadRow = styled(Grid)`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};

  @media (max-width: 560px) {
    display: none;
  }
`;

interface Props {
  rows: SupplyMaterialRow[];
  onChange: (rows: SupplyMaterialRow[]) => void;
}

/**
 * Список материалов заявки в снабжение — свободным текстом: наименование,
 * сколько и в чём. Необязателен, если заявка идёт по документам РД.
 */
export function SupplyMaterialsEditor({ rows, onChange }: Props) {
  const nameRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Новая строка добавляется над кнопкой — переводим в неё фокус, чтобы Tab
  // не уводил дальше по форме мимо только что добавленных полей.
  const focusRow = useRef<number | null>(null);

  useEffect(() => {
    if (focusRow.current !== null) {
      nameRefs.current[focusRow.current]?.focus();
      focusRow.current = null;
    }
  }, [rows.length]);

  const set = (i: number, patch: Partial<SupplyMaterialRow>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const add = () => {
    focusRow.current = rows.length;
    onChange([...rows, { ...EMPTY_MATERIAL_ROW }]);
  };

  return (
    <Wrap>
      <Title>Материалы — если нужно перечислить (необязательно)</Title>
      {rows.length > 0 && (
        <HeadRow aria-hidden="true">
          <span>Наименование, марка</span>
          <span>Сколько</span>
          <span>Ед. изм.</span>
          <span />
        </HeadRow>
      )}
      {rows.map((r, i) => (
        <Grid key={i}>
          <Input
            ref={(el) => {
              nameRefs.current[i] = el;
            }}
            value={r.name}
            onChange={(e) => set(i, { name: e.target.value })}
            placeholder="Наименование, марка"
            aria-label={`Материал ${i + 1}: наименование`}
          />
          <Input
            value={r.quantity}
            onChange={(e) => set(i, { quantity: e.target.value })}
            placeholder="Сколько"
            aria-label={`Материал ${i + 1}: сколько`}
          />
          <Input
            value={r.unit}
            onChange={(e) => set(i, { unit: e.target.value })}
            placeholder="Ед."
            aria-label={`Материал ${i + 1}: единица измерения`}
          />
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
            aria-label={`Убрать материал ${i + 1}`}
          >
            Убрать
          </Button>
        </Grid>
      ))}
      <div>
        <Button type="button" $variant="secondary" $size="sm" onClick={add}>
          <span aria-hidden="true">+</span> Добавить материал
        </Button>
      </div>
    </Wrap>
  );
}
